/**
 * Product Image Lightbox
 * Provides full-screen image viewing with gallery navigation
 */
class ProductImageLightbox {
  constructor() {
    this.lightbox = null;
    this.currentIndex = 0;
    this.images = [];
    this.isOpen = false;
    this.init();
  }

  init() {
    this.createLightboxHTML();
    this.attachEventListeners();
  }

  createLightboxHTML() {
    const lightboxHTML = `
      <div class="product-lightbox" aria-hidden="true">
        <div class="product-lightbox__overlay"></div>
        <div class="product-lightbox__container">
          <button class="product-lightbox__close" aria-label="Close lightbox">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          <button class="product-lightbox__prev" aria-label="Previous image">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          
          <button class="product-lightbox__next" aria-label="Next image">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
          
          <div class="product-lightbox__content">
            <div class="product-lightbox__image-wrapper">
              <img class="product-lightbox__image" src="" alt="" />
              <div class="product-lightbox__loader">
                <svg xmlns="http://www.w3.org/2000/svg" class="product-lightbox__spinner" viewBox="0 0 66 66">
                  <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/>
                </svg>
              </div>
            </div>
            <div class="product-lightbox__counter">
              <span class="product-lightbox__current">1</span>
              <span class="product-lightbox__separator">/</span>
              <span class="product-lightbox__total">1</span>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', lightboxHTML);
    this.lightbox = document.querySelector('.product-lightbox');
  }

  attachEventListeners() {
    // Close button
    const closeBtn = this.lightbox.querySelector('.product-lightbox__close');
    closeBtn.addEventListener('click', () => this.close());

    // Overlay click
    const overlay = this.lightbox.querySelector('.product-lightbox__overlay');
    overlay.addEventListener('click', () => this.close());

    // Navigation buttons
    const prevBtn = this.lightbox.querySelector('.product-lightbox__prev');
    const nextBtn = this.lightbox.querySelector('.product-lightbox__next');
    
    prevBtn.addEventListener('click', () => this.navigate('prev'));
    nextBtn.addEventListener('click', () => this.navigate('next'));

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      
      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'ArrowLeft') {
        this.navigate('prev');
      } else if (e.key === 'ArrowRight') {
        this.navigate('next');
      }
    });

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;
    
    const imageWrapper = this.lightbox.querySelector('.product-lightbox__image-wrapper');
    
    imageWrapper.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    imageWrapper.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.navigate('next');
      } else {
        this.navigate('prev');
      }
    }
  }

  open(images, startIndex = 0) {
    this.images = images;
    this.currentIndex = startIndex;
    this.isOpen = true;

    // Show lightbox immediately
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Show initial loader
    const loader = this.lightbox.querySelector('.product-lightbox__loader');
    loader.classList.add('visible');

    // Update lightbox content
    this.updateImage();
    this.updateCounter();
    this.updateNavigationButtons();
    
    // Trigger animation
    requestAnimationFrame(() => {
      this.lightbox.classList.add('product-lightbox--active');
    });
  }

  close() {
    this.isOpen = false;
    this.lightbox.classList.remove('product-lightbox--active');
    
    setTimeout(() => {
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }, 300);
  }

  navigate(direction) {
    if (direction === 'prev') {
      this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    } else {
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
    }
    
    // Show loader immediately
    const loader = this.lightbox.querySelector('.product-lightbox__loader');
    loader.classList.add('visible');
    
    this.updateImage();
    this.updateCounter();
  }

  updateImage() {
    const img = this.lightbox.querySelector('.product-lightbox__image');
    const loader = this.lightbox.querySelector('.product-lightbox__loader');
    const currentImage = this.images[this.currentIndex];
    
    // Show loader
    loader.classList.add('visible');
    
    // Load new image
    const newImg = new Image();
    newImg.onload = () => {
      img.src = currentImage.src;
      img.srcset = currentImage.srcset || '';
      img.sizes = currentImage.sizes || '';
      img.alt = currentImage.alt || '';
      loader.classList.remove('visible');
    };
    newImg.onerror = () => {
      loader.classList.remove('visible');
    };
    newImg.src = currentImage.src;
  }

  updateCounter() {
    const current = this.lightbox.querySelector('.product-lightbox__current');
    const total = this.lightbox.querySelector('.product-lightbox__total');
    
    current.textContent = this.currentIndex + 1;
    total.textContent = this.images.length;
  }

  updateNavigationButtons() {
    const prevBtn = this.lightbox.querySelector('.product-lightbox__prev');
    const nextBtn = this.lightbox.querySelector('.product-lightbox__next');
    
    // Hide navigation if only one image
    if (this.images.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    } else {
      prevBtn.style.display = '';
      nextBtn.style.display = '';
    }
  }
}

// Initialize lightbox when DOM is ready
let lightboxInstance = null;

document.addEventListener('DOMContentLoaded', () => {
  // Check if lightbox is enabled via data attribute
  const productGallery = document.querySelector('[data-lightbox-enabled="true"]');
  
  if (!productGallery) {
    return; // Lightbox is disabled
  }

  lightboxInstance = new ProductImageLightbox();

  // Attach click handlers to main image and thumbnails
  const mainImage = document.querySelector('.luxury-product__main-image');
  const thumbnails = document.querySelectorAll('.luxury-product__thumbnail');

  if (mainImage) {
    mainImage.style.cursor = 'zoom-in';
    
    mainImage.addEventListener('click', () => {
      const images = collectProductImages();
      const currentIndex = getCurrentImageIndex();
      lightboxInstance.open(images, currentIndex);
    });
  }

  thumbnails.forEach((thumbnail, index) => {
    thumbnail.style.cursor = 'zoom-in';
    
    thumbnail.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      const images = collectProductImages();
      lightboxInstance.open(images, index);
    });
  });
});

function collectProductImages() {
  const images = [];
  const thumbnails = document.querySelectorAll('.luxury-product__thumbnail img');
  
  thumbnails.forEach(img => {
    images.push({
      src: img.dataset.full || img.src,
      srcset: img.dataset.srcset || '',
      sizes: img.dataset.sizes || '',
      alt: img.alt || '',
      thumbSrc: img.src
    });
  });
  
  // If no thumbnails, use main image
  if (images.length === 0) {
    const mainImg = document.querySelector('.luxury-product__main-image img');
    if (mainImg) {
      images.push({
        src: mainImg.src,
        srcset: mainImg.srcset || '',
        sizes: mainImg.sizes || '',
        alt: mainImg.alt || '',
        thumbSrc: mainImg.src
      });
    }
  }
  
  return images;
}

function getCurrentImageIndex() {
  const activeThumbnail = document.querySelector('.luxury-product__thumbnail.active');
  if (!activeThumbnail) return 0;
  
  const allThumbnails = Array.from(document.querySelectorAll('.luxury-product__thumbnail'));
  return allThumbnails.indexOf(activeThumbnail);
}

// Export for potential external use
if (typeof window !== 'undefined') {
  window.ProductImageLightbox = ProductImageLightbox;
}
